import { devLog, devWarn, devError } from '../utils/devLogger';

// 카카오 SDK 초기화 및 유틸리티 함수
declare global {
  interface Window {
    Kakao: any;
    isFlutterApp?: boolean;
    FlutterBridge?: {
      postMessage?: (message: string) => void;
    };
  }
}

const isFlutterWebViewEnvironment = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return Boolean(
    window.isFlutterApp === true ||
    typeof window.FlutterBridge !== 'undefined' ||
    userAgent.includes('Flutter') ||
    userAgent.includes('FlutterWebView')
  );
};

// 카카오 SDK 초기화
export const initKakaoSDK = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (!window.Kakao) {
      devWarn('카카오 SDK가 아직 로드되지 않았습니다.');
      return false;
    }

    const appKey = process.env.REACT_APP_KAKAO_APP_KEY;
    if (!appKey) {
      devError('REACT_APP_KAKAO_APP_KEY가 설정되지 않았습니다.');
      return false;
    }
    
    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(appKey);
      devLog('카카오 SDK 초기화 완료');
    }
    return true;
  } catch (error) {
    devError('카카오 SDK 초기화 실패:', error);
    return false;
  }
};

// 카카오 로그인
export const kakaoLogin = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.Kakao?.Auth) {
        reject(new Error('카카오 SDK가 로드되지 않았습니다.'));
        return;
      }

      const isFlutterWebView = isFlutterWebViewEnvironment();
      const auth = window.Kakao.Auth;

      // Flutter WebView에서는 popup/proxy 경로가 불안정하므로 loginForm을 우선 사용
      const primaryLoginFn =
        isFlutterWebView && typeof auth.loginForm === 'function'
          ? auth.loginForm.bind(auth)
          : auth.login.bind(auth);

      const fallbackLoginFn =
        typeof auth.loginForm === 'function' ? auth.loginForm.bind(auth) : null;

      const handleKakaoSuccess = (response: any) => {
        devLog('카카오 로그인 성공:', response);
        // 사용자 정보 가져오기
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: (userInfo: any) => {
            devLog('카카오 사용자 정보:', userInfo);
            resolve({
              accessToken: response.access_token,
              userInfo: userInfo,
              providerId: userInfo.id.toString()
            });
          },
          fail: (error: any) => {
            devError('카카오 사용자 정보 조회 실패:', error);
            reject(error);
          }
        });
      };

      const createLoginOptions = (withFallbackRetry: boolean) => ({
        throughTalk: !isFlutterWebView,
        persistAccessToken: true,
        success: handleKakaoSuccess,
        fail: (error: any) => {
          const errorText = (error?.error_description || error?.message || '').toString();
          const shouldRetryWithForm =
            withFallbackRetry &&
            fallbackLoginFn !== null &&
            errorText.includes('getKakaoAgent');

          if (shouldRetryWithForm) {
            devWarn('카카오 popup/proxy 경로 실패, loginForm으로 재시도:', error);
            fallbackLoginFn!(createLoginOptions(false));
            return;
          }

          devError('카카오 로그인 실패:', error);
          reject(error);
        }
      });

      primaryLoginFn(createLoginOptions(true));
    } catch (error) {
      devError('카카오 로그인 실행 중 오류:', error);
      reject(error);
    }
  });
};

// 카카오 로그아웃
export const kakaoLogout = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!window.Kakao?.Auth) {
      reject(new Error('카카오 SDK가 로드되지 않았습니다.'));
      return;
    }

    window.Kakao.Auth.logout(() => {
      devLog('카카오 로그아웃 완료');
      resolve();
    });
  });
};
